import React, { ComponentType, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavBar from "../../../../components/nav-bar/nav-bar";
import Container from "../../../../components/container/container";
import {
  MyNexarContext,
  MyNexarContextType,
} from "../../../../components/my-nexar-context/my-nexar-context";
import InactiveForm from "../../components/subscription-form/inactive-form";
import {
  Subscription,
  SubscriptionStatus,
} from "../../../../contracts/nexar-api/types";
import { APP_BASE_PATHNAMES } from "../../../../core/consts/consts";
import { SUBSCRIPTION_FORM_FIELDS } from "../../components/subscription-form/formik/formik";
import CancelModal from "./modals/cancel-modal/cancel-modal";
import SuccessfulActivationModal from "./modals/successful-activation-modal/successful-activation-modal";
import styles from "./subscription-details.module.scss";

import Event from "../../../../core/consts/analytics-events";
import SubscriptionCard from "../../components/subscription-card/subscription-card";
import Modal from "../../../../components/modal/modal";
import { useTranslation } from "react-i18next";
import { useDI } from "../../../../core/di";
import ExpiredForm from "../../components/subscription-form/expired-form";
import ActiveForm from "../../components/subscription-form/active-form";
import CancelPrompt from "./prompts/cancel-prompt/cancel-prompt";
import throttle from "lodash.throttle";
import MyNexarStripeForm from "../../components/subscription-form/stripe-form/stripe-form";
import MyNexarRecurlyForm from "../../components/subscription-form/recurly-form/recurly-form";
import SUBSCRIPTION_ERRORS from "../../../../core/consts/subscription-errors";
import { HttpError } from "../../../../core/errors/errors";
import LoadFailureModal from "./modals/load-failure-modal/load-failure-modal";
import { Campaigns } from "../../../../core/consts/campains";

export function SubscriptionDetails() {
  let { subscriptionId } = useParams();
  const navigate = useNavigate();
  const context = useContext<MyNexarContextType>(MyNexarContext);
  const [subscription, setSubscription] = useState<Subscription>();
  const [showModal, setShowModal] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState<boolean>(false);
  const { t } = useTranslation();
  const { analytics, nexarApi, logger } = useDI();
  const [loadFailure, setLoadFailure] = useState(false);

  useEffect(() => {
    async function getSubscription() {
      if (subscriptionId) {
        try {
          const { data } = await nexarApi.getSubscriptionById({
            subscriptionId,
          });
          setSubscription(data);
        } catch (error) {
          // subscription not available
          if (error instanceof HttpError) {
            setLoadFailure(true);
          }
        }
      }
    }
    const trackPageScroll = throttle(
      function () {
        analytics?.track(Event.SubscriptionDetailsScrolled);
      },
      1000,
      {
        leading: true,
        trailing: false,
      }
    );

    analytics?.track(Event.SubscriptionDetailsViewed);
    window.addEventListener("scroll", trackPageScroll);

    getSubscription();

    return () => {
      window.removeEventListener("scroll", trackPageScroll);
    };
  }, [subscriptionId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showModal, showCancelPrompt]);

  const getSubscriptionConfigByStatusAndPlatform = (
    status: SubscriptionStatus,
    isStripe: boolean = false
  ) => {
    // Do nothing, let the callback or redirection do it's work
    const handleStripeUpdate = () => Promise.resolve({ success: {} });
    const handleRecurlyUpdate = nexarApi.updateSubscriptionById;
    const handleRecurlyActivation = nexarApi.activateSubscriptionById;

    switch (status) {
      case "PENDING":
      case "INACTIVE":
      case "PAUSED":
      case "PENDING_CANCELLATION":
        return {
          action: isStripe ? handleStripeUpdate : handleRecurlyActivation,
          errorMessage: "weCouldNotActivateYourSubscription",
          successMessage: "subscriptionActivated",
          mixpanelEvent: Event.ConnectActivateFailed,
        };
      case "ACTIVE":
        return {
          action: isStripe ? handleStripeUpdate : handleRecurlyUpdate,
          errorMessage: "weCouldNotUpdateYourPaymentInformation",
          successMessage: "paymentInfoUpdated",
          mixpanelEvent: Event.ConnectEditFailed,
        };
    }
  };
  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleShowCancelPrompt = () => {
    setShowCancelPrompt(true);
  };
  const handleHideCancelPrompt = () => {
    setShowCancelPrompt(false);
  };

  const handleErrorAndNavigate = (
    errorMessage: string,
    errorDocUrl: string
  ) => {
    context.setSubscriptionError(errorMessage, errorDocUrl);
    navigateToConnectPage();
  };

  const handleSubscriptionAction = async () => {
    if (subscription?.status === "ACTIVE") {
      handleSuccessAndNavigate();
    } else {
      handleShowModal();
    }
  };

  const handleSubscriptionError = (exception: any) => {
    let error;
    if (subscription?.status === "ACTIVE") {
      error = SUBSCRIPTION_ERRORS.COULDNT_UPDATE;
    } else {
      error = SUBSCRIPTION_ERRORS.COULDNT_ACTIVATE;
    }

    handleErrorAndNavigate(t(error.message), error.docUrl);
  };

  const handleDeleteSubscription = async ({ cause }: { cause: string }) => {
    if (subscriptionId) {
      try {
        analytics?.track(Event.CancelSubscriptionYesClicked, {
          subscriptionId,
          cause,
        });
        const { success: successFromResponse, error: errorFromResponse } =
          await nexarApi.deleteSubscriptionById({
            subscriptionId,
          });
        if (successFromResponse) {
          handleCancelSuccessAndNavigate();
        } else {
          throw errorFromResponse;
        }
      } catch (error) {
        analytics?.track(Event.CancelSubscriptionFailed, {
          subscriptionId,
          error,
        });
        logger?.Error(new Error("Failed to handle subscription cancellation"), {
          subscription: {
            id: subscriptionId,
          },
          error,
        });
        const subscriptionError = SUBSCRIPTION_ERRORS.COULDNT_CANCEL;
        handleErrorAndNavigate(
          t(subscriptionError.message),
          subscriptionError.docUrl
        );
      }
    }
  };

  const navigateToConnectPage = () => {
    navigate("/connect", { replace: true });
  };

  const handleSuccessAndNavigate = () => {
    if (subscription?.status) {
      const { successMessage } = getSubscriptionConfigByStatusAndPlatform(
        subscription.status
      );
      context.setSubscriptionSuccessMessage(successMessage);
    }
    navigateToConnectPage();
  };

  const handleCancelSuccessAndNavigate = () => {
    context.setSubscriptionSuccessMessage(t("subscriptionCancelled"));
    navigateToConnectPage();
  };

  const renderModal = () => {
    if (subscription?.status === "ACTIVE") {
      return (
        <CancelModal
          onClose={navigateToConnectPage}
          onCancelSubscription={handleDeleteSubscription}
        />
      );
    } else {
      return (
        <SuccessfulActivationModal
          onClose={navigateToConnectPage}
          onFinish={handleSuccessAndNavigate}
        />
      );
    }
  };
  function handleCancelSubscription() {
    analytics?.track(Event.SubscriptionDetailsCancelSubscriptionClicked);
    if (subscription?.subscriptionQuantity === 1) {
      handleProceedCancelAllSubscriptions();
    } else {
      handleShowCancelPrompt();
    }
  }

  function handleProceedCancelAllSubscriptions() {
    analytics?.track(Event.CancelSubscriptionPromptAllClicked);
    handleHideCancelPrompt();
    handleShowModal();
  }
  function handleProceedCancelOneSubscription() {
    analytics?.track(Event.CancelSubscriptionPromptOneClicked);
  }

  const renderForm = () => {
    if (!subscriptionId) return null;

    const platformForm = subscription?.stripeSubscriptionId
      ? MyNexarStripeForm
      : MyNexarRecurlyForm;

    switch (subscription?.status) {
      case "ACTIVE":
        return (
          <ActiveForm
            onCancelSubscription={handleCancelSubscription}
            onUpdateSubscription={handleSubscriptionAction}
            onUpdateSubscriptionError={handleSubscriptionError}
            form={platformForm}
            subscriptionId={subscriptionId}
          />
        );
      case "INACTIVE":
        switch (subscription?.cancellationCause) {
          case "PAYMENT_FAILURE":
            return (
              <ExpiredForm
                onActivateSubscription={handleSubscriptionAction}
                onActivateSubscriptionError={handleSubscriptionError}
                form={platformForm}
                subscriptionId={subscriptionId}
              />
            );
          default:
            return (
              <InactiveForm
                onActivateSubscription={handleSubscriptionAction}
                onActivateSubscriptionError={handleSubscriptionError}
                form={platformForm}
                subscriptionId={subscriptionId}
              />
            );
        }
      case "PENDING_CANCELLATION": // This is a stripe only state, so for recurly it will try to activate, for stripe too but this maps to update
      case "PAUSED":
      case "PENDING":
        return (
          <InactiveForm
            onActivateSubscription={handleSubscriptionAction}
            onActivateSubscriptionError={handleSubscriptionError}
            form={platformForm}
            subscriptionId={subscriptionId}
          />
        );
    }
  };

  const handleNavbackClick = () => {
    analytics?.track(Event.SubscriptionDetailsBackClicked);
  };

  const modalTitle = t("nexarConnectDetails");
  const cancelOneHref =
    "https://intercom.help/nexar/en/articles/8601544-contact-us";

  if (loadFailure) {
    return (
      <LoadFailureModal
        modalTitle={modalTitle}
        onNavbackClick={handleNavbackClick}
      />
    );
  }
  return (
    <Container>
      {showCancelPrompt ? (
        <CancelPrompt
          onCancelAll={handleProceedCancelAllSubscriptions}
          onCancelOne={handleProceedCancelOneSubscription}
          cancelOneHref={cancelOneHref}
          onClose={handleHideCancelPrompt}
        />
      ) : (
        <></>
      )}
      {showModal ? (
        renderModal()
      ) : (
        <Modal
          fullWidth={true}
          title={modalTitle}
          data-testid={"subscription-details"}
          navBar={
            <NavBar
              mode={"back"}
              to={`/${APP_BASE_PATHNAMES.CONNECT}`}
              onClick={handleNavbackClick}
            />
          }
        >
          <SubscriptionCard
            subscription={subscription}
            className={styles.SubCard}
            contentOnly
          />
          {renderForm()}
        </Modal>
      )}
    </Container>
  );
}

export default SubscriptionDetails;
